import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPosts, getCategories } from '../../../detail/BlogDetailPage/BlogIndex';
import { Calendar, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Newsroom() {
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 9;

    const categories = getCategories();
    const allPosts = getAllPosts(); // newest first: 6,5,4,3,2,1

    // Filter posts based on active category
    const filteredPosts = activeCategory === "All"
        ? allPosts
        : allPosts.filter(post => post.category === activeCategory);

    // Calculate pagination
    const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

    // Handle category change
    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setCurrentPage(1);
    };

    // Handle post click - navigate to detail page using slug
    const handlePostClick = (slug) => {
        navigate(`/newsroom/${slug}`);
    };

    // Generate page numbers
    const getPageNumbers = () => {
        const pages = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <div>
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-4 mb-12">
                    {categories.map((category) => (
                        <button
                            key={category}
                            onClick={() => handleCategoryChange(category)}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                                activeCategory === category
                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/50'
                                    : 'bg-white/10 text-white/80 hover:bg-white/20 backdrop-blur-sm'
                            }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {/* Blog Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {currentPosts.map((post, index) => (
                        <article
                            key={post.id}
                            onClick={() => handlePostClick(post.slug)}
                            className="group bg-slate-900/50 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/20 hover:-translate-y-1 cursor-pointer"
                            style={{
                                animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`
                            }}
                        >
                            {/* Image */}
                            <div className="relative h-48 overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600">
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent"></div>
                                
                                {/* Category Badge */}
                                <div className="absolute top-4 left-4">
                                    <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-semibold rounded-full">
                                        {post.category}
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                                <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
                                    <time className="flex items-center gap-1">
                                        <Calendar className="w-4 h-4" />
                                        {post.date}
                                    </time>
                                </div>
                                <h2 className="text-xl font-bold text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                                    {post.title}
                                </h2>
                                <p className="text-sm text-slate-400 line-clamp-2 mb-4">
                                    {post.description}
                                </p>
                                 <div className="flex items-center gap-2 text-blue-400 font-semibold text-sm group/link">
                                    Read more
                                    <ArrowRight size={16} className="group-hover/link:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-lg bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            aria-label="Previous page"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>

                        {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className="px-3 py-2 text-white/60">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`min-w-[40px] h-10 rounded-lg font-medium transition-all ${
                                        currentPage === page
                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/50'
                                            : 'bg-white/10 text-white/80 hover:bg-white/20 backdrop-blur-sm'
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        ))}

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-lg bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                            aria-label="Next page"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>

            {/* CSS Animation */}
            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
}